// src/components/widgets/EmbedCodeModal.jsx
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Box,
  Typography,
  Link,
  Tabs, // For switching between snippet types
  Tab,  // For switching between snippet types
} from '@mui/material';
import { toast } from 'react-toastify';
import CopyWithFeedbackButton from '../common/CopyWithFeedbackButton';
import { Link as RouterLink } from 'react-router-dom'; // If you want to link to template settings

// Ensure this points to the ROOT URL of your platform deployment
// e.g., https://dpmg.xyz (without /api/v1)
const PLATFORM_ROOT_URL = process.env.REACT_APP_API_URL || '';

// This should be the path to where loader-v2.js is served from your backend's static files.
// If your backend serves it from /static/loader-v2.js, this is correct.
const LOADER_SCRIPT_PUBLIC_PATH = '/static/loader-v2.js';

const EmbedCodeModal = ({ open, onClose, versionId, templateId, templateName }) => {
  const [snippetForVersion, setSnippetForVersion] = useState('');
  const [snippetForTemplate, setSnippetForTemplate] = useState('');
  const [error, setError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0); // 0 for version, 1 for template

  useEffect(() => {
    setSnippetForVersion('');
    setSnippetForTemplate('');
    setError(null);

    if (open) {
      if (!PLATFORM_ROOT_URL) {
        const configError = "Configuration Error: Your platform's root URL (REACT_APP_API_URL) is not set. Cannot generate snippets.";
        setError(configError);
        toast.error("Platform URL not configured. Cannot generate embed code.");
        return;
      }
      if (!LOADER_SCRIPT_PUBLIC_PATH) {
        const pathError = "Configuration Error: Loader script path is not defined.";
        setError(pathError);
        toast.error("Loader script path not configured.");
        return;
      }

      const mountId = 'banty-widget-mount-point'; // Default mount point ID
      // The loader URL should be constructed relative to the platform's root URL.
      // If PLATFORM_ROOT_URL is "http://localhost:8000" and LOADER_SCRIPT_PUBLIC_PATH is "/static/loader-v2.js",
      // then loaderUrl will be "http://localhost:8000/static/loader-v2.js"
      const loaderUrl = `${PLATFORM_ROOT_URL}${LOADER_SCRIPT_PUBLIC_PATH}`;

      // Snippet for specific version
      if (versionId) {
        const generatedVersionSnippet = `<!-- 1. Add this div where you want the widget to appear -->
<div id="${mountId}"></div>

<!-- 2. Add this script tag, preferably at the end of your <body> -->
<script 
  src="${loaderUrl}" 
  data-widget-id="${versionId}"
  data-mount-id="${mountId}"
  data-platform-api-base="${PLATFORM_ROOT_URL}" <!-- Important for self-hosting/proxies -->
  async 
  defer>
</script>`;
        setSnippetForVersion(generatedVersionSnippet);
      } else {
        // If only templateId is available, we might default the tab or show a message
        // For now, we'll just not generate this snippet if versionId is missing
      }

      // Snippet for template (active version)
      if (templateId) {
        const generatedTemplateSnippet = `<!-- 1. Add this div where you want the widget to appear -->
<div id="${mountId}"></div>

<!-- 2. Add this script tag, preferably at the end of your <body> -->
<!-- This will always load the ACTIVE version of the template -->
<script 
  src="${loaderUrl}" 
  data-template-id="${templateId}"
  data-mount-id="${mountId}"
  data-platform-api-base="${PLATFORM_ROOT_URL}" <!-- Important for self-hosting/proxies -->
  async 
  defer>
</script>`;
        setSnippetForTemplate(generatedTemplateSnippet);
      }

      if (!versionId && !templateId) {
          setError("Cannot generate snippet: Widget Version ID or Template ID is missing.");
      }

    }
  }, [open, versionId, templateId]);

  const handleClose = () => {
    onClose();
    setCurrentTab(0); // Reset tab on close
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const currentSnippet = currentTab === 0 ? snippetForVersion : snippetForTemplate;
  const canShowVersionSnippet = !!versionId;
  const canShowTemplateSnippet = !!templateId;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Embed Widget on Your Website</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {(!versionId && !templateId && !error) && (
             <Alert severity="warning">Widget Version ID or Template ID is missing. Cannot display embed code.</Alert>
        )}

        {(versionId || templateId) && !error && (
          <>
            <Alert severity="info" sx={{ my: 2 }}>
              <strong>Important Security Note:</strong> This widget uses domain verification. 
              Ensure you have registered the hostname(s) (e.g., `www.yourwebsite.com`) 
              where you will embed this widget in the Widget Template settings ({templateName || 'relevant template'}). 
              Requests from unregistered domains will be blocked.
              {templateId && (
                <Typography variant="body2" sx={{mt: 1}}>
                  <Link component={RouterLink} to={`/widgets/templates/${templateId}/settings`} underline="hover">
                    Manage Allowed Domains for '{templateName || `Template ID: ...${templateId.slice(-6)}`}'
                  </Link>
                </Typography>
              )}
            </Alert>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={currentTab} onChange={handleTabChange} aria-label="embed snippet type">
                <Tab label="Embed Specific Version" id="tab-version" disabled={!canShowVersionSnippet} />
                <Tab label="Embed Template (Active Version)" id="tab-template" disabled={!canShowTemplateSnippet}/>
              </Tabs>
            </Box>

            {currentSnippet ? (
              <Box mt={1}>
                <Typography variant="body1" gutterBottom sx={{mb:1}}>
                  {currentTab === 0 
                    ? `This snippet will always load widget version ID: ${versionId}.`
                    : `This snippet will always load the active version of template: ${templateName || templateId}.`}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Copy and paste the following HTML code snippet into your website.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  value={currentSnippet}
                  InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
                  variant="outlined"
                  minRows={8}
                  maxRows={15}
                  sx={{ mb: 1, backgroundColor: (theme) => theme.palette.action.hover }}
                />
                <CopyWithFeedbackButton
                  fullWidth
                  variant="contained"
                  textToCopy={currentSnippet}
                >
                  Copy Snippet
                </CopyWithFeedbackButton>
              </Box>
            ) : (
                 <Box display="flex" justifyContent="center" p={3}>
                    {( (currentTab === 0 && !canShowVersionSnippet) || (currentTab === 1 && !canShowTemplateSnippet) ) 
                        ? <Typography>This embed option is not available for the current selection.</Typography>
                        : <CircularProgress />
                    }
                 </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmbedCodeModal;